import { css } from "@emotion/react";
import styled from "@emotion/styled";
import MuiTab, { type TabProps } from "@mui/material/Tab";
import MuiTabs from "@mui/material/Tabs";
import { useImperativeHandle, useRef, type ReactNode, type RefObject } from "react";
import { Link } from "react-router";

import { DEFAULT_INPUT_HEIGHT, FONT_WEIGHT_SEMI_BOLD } from "../utilities";

export const Tabs = MuiTabs;

export const Tab = MuiTab;

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  keepMounted?: boolean;
  ref?: RefObject<TabPanelActions>;
}

export type TabPanelActions = {
  resetScroll: () => void;
};

export const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, keepMounted, ref: tabPanelActions, ...other } = props;

  const ref = useRef<HTMLDivElement>(null);

  useImperativeHandle(tabPanelActions, () => ({ resetScroll: () => ref.current?.scrollTo({ top: 0 }) }), []);

  return (
    <div
      ref={ref}
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      css={css`
        ${value !== index && `display: none;`}
        scroll-behavior: smooth;
      `}
      {...other}
    >
      {(keepMounted || value === index) && children}
    </div>
  );
};

export const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const TabsHeaderContainer = styled.div`
  border-bottom: solid 1px ${({ theme }) => theme.outlineSoft};
`;

export const TabsContentContainer = styled.div`
  overflow-y: auto;
`;

export const LinkTab = ({ children, ...rest }: TabProps<typeof Link, { component?: typeof Link }>) => {
  return (
    <Tab component={Link} {...rest}>
      {children}
    </Tab>
  );
};

const tabHorizontalPadding = 8;

export const PropertiesPanelTabs = styled(Tabs)`
  display: flex;
  position: relative;
  height: 100%;
  overflow: hidden;
  padding: 8px ${tabHorizontalPadding}px 0;
  min-height: ${DEFAULT_INPUT_HEIGHT + 8}px;
  &:after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    right: 0;
    background: ${({ theme }) => theme.outlineSoft};
    height: 2px;
    z-index: 1;
  }
  .MuiTabs-indicator {
    z-index: 2;
  }
`;

export const PropertiesPanelTab = styled(Tab)`
  padding: 4px ${tabHorizontalPadding}px;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  min-height: ${DEFAULT_INPUT_HEIGHT}px;
  flex: 0 0 auto;
  font-size: ${({ theme }) => theme.fontSizeSmall};
  text-align: center;
  min-width: 0;
`;
